// src/components/TheFloor.jsx
// The Floor — Unified AI Workspace
// Natural conversation with Victor, Selma, and Cyrano.
// No mode selection. No account picker gate. Just talk.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TheFloor.css';
import { apiFetch } from '../lib/api';
import { useProfile } from '../context/UserProfileContext';
import { toProbabilityLabel } from '../config/stages';   // B92

const AI_CONFIG = {
  victor: { name: 'Victor',  icon: '⚡', color: 'var(--dm-indigo)', label: 'Strategy',       bg: '#f0f1ff' },
  selma:  { name: 'Selma',   icon: '🔍', color: 'var(--dm-cyan)', label: 'Intelligence',   bg: '#ecfeff' },
  cyrano: { name: 'Cyrano',  icon: '✍️', color: 'var(--dm-purple-vivid)', label: 'Communications', bg: 'var(--dm-purple-pale)' },
  quinn:  { name: 'Quinn',   icon: '👑', color: 'var(--dm-amber)', label: 'Executive',      bg: 'var(--dm-amber-wash)' },
  jarvis: { name: 'Jarvis',  icon: '🤖', color: '#64748b', label: '',               bg: '#f8fafc' },
};

// ── Markdown renderer (reused from AIPursuitChat) ─────────────────────────────
function MarkdownContent({ content }) {
  if (!content) return null;
  const lines    = content.split('\n');
  const elements = [];
  let i = 0, key = 0;

  const inlineRender = (text) => {
    const tokens = [];
    const allRe  = /(\*\*|__)(.+?)\1|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|`([^`]+)`/g;
    let lastIndex = 0, m;
    while ((m = allRe.exec(text)) !== null) {
      if (m.index > lastIndex) tokens.push({ type: 'text', content: text.slice(lastIndex, m.index) });
      if (m[1])      tokens.push({ type: 'bold',   content: m[2] });
      else if (m[3]) tokens.push({ type: 'italic', content: m[3] });
      else if (m[4]) tokens.push({ type: 'code',   content: m[4] });
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) tokens.push({ type: 'text', content: text.slice(lastIndex) });
    if (!tokens.length) tokens.push({ type: 'text', content: text });
    return tokens.map((t, idx) => {
      if (t.type === 'bold')   return <strong key={idx}>{t.content}</strong>;
      if (t.type === 'italic') return <em key={idx}>{t.content}</em>;
      if (t.type === 'code')   return <code key={idx} className="tf-inline-code">{t.content}</code>;
      return <span key={idx}>{t.content}</span>;
    });
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim())                        { i++; continue; }
    if (/^---+$/.test(line.trim()))          { elements.push(<hr key={key++} className="tf-hr" />); i++; continue; }
    if (line.trim().startsWith('```'))       {
      const codeLines = []; i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { codeLines.push(lines[i]); i++; }
      i++;
      elements.push(<pre key={key++} className="tf-code-block"><code>{codeLines.join('\n')}</code></pre>);
      continue;
    }
    const hm = line.match(/^(#{1,3})\s+(.+)/);
    if (hm) {
      const Tag = `h${hm[1].length + 2}`;
      elements.push(<Tag key={key++} className={`tf-h${hm[1].length}`}>{inlineRender(hm[2])}</Tag>); i++; continue;
    }
    if (line.startsWith('> ')) {
      const ql = []; while (i < lines.length && lines[i].startsWith('> ')) { ql.push(lines[i].slice(2)); i++; }
      elements.push(<blockquote key={key++} className="tf-blockquote">{ql.map((l,x) => <p key={x}>{inlineRender(l)}</p>)}</blockquote>); continue;
    }
    if (/^[-*+] /.test(line)) {
      const items = []; while (i < lines.length && /^[-*+] /.test(lines[i])) { items.push(lines[i].replace(/^[-*+] /,'')); i++; }
      elements.push(<ul key={key++} className="tf-ul">{items.map((it,x)=><li key={x}>{inlineRender(it)}</li>)}</ul>); continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = []; while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/,'')); i++; }
      elements.push(<ol key={key++} className="tf-ol">{items.map((it,x)=><li key={x}>{inlineRender(it)}</li>)}</ol>); continue;
    }
    const pl = [];
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith('#') && !lines[i].startsWith('> ') && !/^[-*+] /.test(lines[i]) && !/^\d+\.\s/.test(lines[i]) && !lines[i].startsWith('```') && !/^---+$/.test(lines[i].trim())) {
      pl.push(lines[i]); i++;
    }
    if (pl.length) elements.push(<p key={key++} className="tf-p">{pl.map((l,x)=><span key={x}>{inlineRender(l)}{x<pl.length-1&&<br/>}</span>)}</p>);
  }
  return <div className="tf-markdown">{elements}</div>;
}

// ── Message bubble ────────────────────────────────────────────────────────────
// ── Parse action tags from AI message content ────────────────────────────────
// Cyrano appends [ACTION:stage_draft] after drafts — subject/body extracted from message
// Quinn appends [ACTION:add_calendar|EventName|DateISO|EndDateISO|EventType|ContactName]
function extractDraftFromText(text) {
  // Extract subject line — looks for "Subject: ..." in the message
  const subjectMatch = text.match(/\*{0,2}Subject:\*{0,2}\s*(.+)/i);
  const subject = subjectMatch ? subjectMatch[1].trim().replace(/\*+/g, '') : 'Draft';
  // Extract body — everything after the subject line up to Cyrano's notes section
  let body = text;
  const notesIdx = text.search(/\*{0,2}Cyrano'?s?\s+notes?:\*{0,2}/i);
  if (notesIdx > -1) body = text.slice(0, notesIdx).trim();
  // Remove the subject line from body
  if (subjectMatch) {
    body = body.replace(/\*{0,2}Subject:\*{0,2}\s*.+\n?/i, '').trim();
  }
  return { subject, body };
}

// ── Parse attr="value" attributes from ACTION tag strings ─────────────────────
function parseAttrs(attrStr) {
  // Normalize escaped quotes that Claude sometimes generates in action tags
  const normalized = attrStr.replace(/\\"/g, '"');
  const get = (key) => {
    const m = normalized.match(new RegExp(key + '="([^"]*)"')) ||
              normalized.match(new RegExp(key + "='([^']*)'" ));
    return m ? m[1].trim() : '';
  };
  return get;
}

function parseActionTag(content) {
  if (!content) return { cleanContent: content, action: null };
  const match = content.match(/\[ACTION:([^\]]+)\]/);
  if (!match) return { cleanContent: content, action: null };
  const cleanContent = content.replace(match[0], '').trim();
  const raw = match[1];
  const actionType = raw.split('|')[0].split(' ')[0]; // handle both pipe and attr formats

  if (actionType === 'stage_draft') {
    const { subject, body } = extractDraftFromText(cleanContent);
    return { cleanContent, action: { type: 'stage_draft', subject, body, draftType: 'Outreach', to: '' }};
  }

  if (actionType === 'add_calendar') {
    const parts = raw.split('|');
    const eventName   = parts[1] || '';
    const startDate   = parts[2] || '';
    const endDate     = parts[3] || '';
    const eventType   = parts[4] || 'Meeting';
    const contactName = parts[5] || '';
    const hasStructuredData = eventName && startDate && startDate.includes('T');
    if (hasStructuredData) {
      return { cleanContent, action: { type: 'add_calendar', eventName: eventName.trim(), date: startDate.trim(), endDate: endDate.trim() || null, eventType: eventType.trim(), contactName: contactName.trim() }};
    }
    const timeMatch = cleanContent.match(/(\d{1,2}(?::\d{2})?\s*(?:AM|PM))/i);
    const dateMatch = cleanContent.match(/(\d{4}-\d{2}-\d{2}|today|tomorrow|monday|tuesday|wednesday|thursday|friday)/i);
    const nameMatch = cleanContent.match(/(?:add|schedule|block|create)\s+(?:a\s+)?(.+?)(?:\s+(?:on|at|for|to))/i);
    return { cleanContent, action: { type: 'add_calendar', eventName: nameMatch ? nameMatch[1].trim() : 'Meeting', rawText: cleanContent, timeHint: timeMatch ? timeMatch[1] : '', dateHint: dateMatch ? dateMatch[1] : 'today' }};
  }

  if (actionType === 'create_task') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:        'create_task',
      name:        get('name'),
      taskType:    get('taskType'),
      dueDate:     get('dueDate'),
      accountName: get('accountName'),
      priority:    get('priority') || 'This Week',
    }};
  }

  if (actionType === 'create_lead') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:        'create_lead',
      name:        get('name'),
      accountName: get('accountName'),
      signal:      get('signal'),
      priority:    get('priority') || 'Medium',
      hypothesis:  get('hypothesis'),
    }};
  }

  if (actionType === 'create_opportunity') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:        'create_opportunity',
      name:        get('name'),
      accountName: get('accountName'),
      dealSize:    get('dealSize'),
      hypothesis:  get('hypothesis'),
      stage:       get('stage') || 'Prospecting',
    }};
  }

  if (actionType === 'update_meddic') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:             'update_meddic',
      metrics:          get('metrics'),
      economicBuyer:    get('economicBuyer'),
      decisionCriteria: get('decisionCriteria'),
      decisionProcess:  get('decisionProcess'),
      identifiedPain:   get('identifiedPain'),
      champion:         get('champion'),
    }};
  }

  if (actionType === 'update_business_case') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:    'update_business_case',
      title:   get('title'),
      summary: get('summary'),
      content: get('content'),
    }};
  }

  if (actionType === 'create_derived_signal') {
    const get = parseAttrs(raw);
    return { cleanContent, action: {
      type:             'create_derived_signal',
      title:            get('title'),
      signalType:       get('signalType') || 'Strategic Inference',
      confidence:       get('confidence') || 'Speculative',
      reasoning:        get('reasoning'),
      interpretation:   get('interpretation'),
      sourceSignalIds:  get('sourceSignalIds').split(',').map(s => s.trim()).filter(Boolean),
    }};
  }

  return { cleanContent, action: null };
}

function MessageBubble({ msg, isStreaming, onAction, context }) {
  // Hooks must always be called unconditionally — before any early returns
  const [actionDone,    setActionDone]    = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionNotice,  setActionNotice]  = React.useState('');
  const [dismissed,     setDismissed]     = React.useState(false);

  // Closing summary — distinct Jarvis-style card, rendered after hooks
  if (msg.isClosingSummary) {
    return (
      <div className="tf-message tf-closing-summary">
        <div className="tf-closing-summary-header">
          <span className="tf-closing-summary-icon">🤖</span>
          <span className="tf-closing-summary-label">Session Summary — Jarvis</span>
        </div>
        <div className="tf-closing-summary-body">
          <MarkdownContent content={msg.content} />
        </div>
      </div>
    );
  }

  const isUser    = msg.role === 'user';
  const isSystem  = msg.role === 'system';
  const isHandoff = isSystem && (msg.content || '').startsWith('→');
  const cfg       = AI_CONFIG[msg.ai] || {};

  if (isHandoff) return (
    <div className="tf-handoff"><span>🔀</span><span>{msg.content}</span></div>
  );
  if (isSystem) return (
    <div className="tf-system-notice"><span>ℹ️</span><span>{msg.content}</span></div>
  );

  const timeStr = msg.timestamp
    ? new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '';

  // Parse action tag from content (only on non-streaming, non-user messages)
  const { cleanContent, action } = (!isUser && !isStreaming)
    ? parseActionTag(msg.content || '')
    : { cleanContent: msg.content, action: null };

  // Single-click actions: stage_draft, add_calendar → floor/action.js
  const handleActionClick = async () => {
    if (!action || actionLoading || actionDone) return;
    setActionLoading(true);
    try {
      const res = await apiFetch('/api/floor/action', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ action: action.type, data: action, context: context || {} }),
      });
      const json = await res.json();
      setActionNotice(json.notice || (json.success ? '✅ Done' : '⚠️ Failed'));
      if (json.success) setActionDone(true);
    } catch (err) {
      setActionNotice('⚠️ Error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Confirm actions: create_task, create_lead, create_opportunity → Jarvis-gated API calls
  const handleConfirmAction = async () => {
    if (!action || actionLoading || actionDone) return;
    setActionLoading(true);
    try {
      const ctx = context || {};
      let endpoint, body;

      if (action.type === 'create_task') {
        let accountId = ctx.accountId || null;
        if (!accountId && action.accountName) {
          const acctRes = await apiFetch('/api/accounts/list');
          const acctData = await acctRes.json();
          if (acctData.accounts) {
            const nameLower = action.accountName.toLowerCase();
            const match = acctData.accounts.find(a =>
              (a.accountName || '').toLowerCase().includes(nameLower) ||
              nameLower.includes((a.accountName || '').toLowerCase())
            );
            if (match) accountId = match.id;
          }
        }

        // ── Jarvis gate: validate before writing ──────────────────────────────
        // Tasks linked to accounts go through Jarvis to check account/opp status
        // and prevent duplicates. Tasks with no account bypass — they're personal tasks.
        if (accountId) {
          const validation = await apiFetch('/api/jarvis/validate-queue-item', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: action.name,
              why: `Task created from Floor session`,
              priority: action.priority === 'Do First' ? '🔴 Hot' : action.priority === 'Do Today' ? '🟡 Important' : '🔵 Watch',
              accountId,
              opportunityId: ctx.opportunityId || null,
              source: 'Manual',
            }),
          }).then(r => { if (!r.ok) throw new Error(`gate returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => null)` followed by `if (validation && …)`,
          // so a failed call skipped every check — account-monitoring, dead
          // opportunity, duplicate overlap and the 3-item cap all silently
          // bypassed. Note this endpoint also WRITES the situation_queue row
          // as part of 'validating' (validate-queue-item.js:132-147), so a
          // failure here means the queue item did not land either — the
          // notice has to say so rather than implying only a check was
          // skipped.
          .catch(err => ({ approved: false, action: 'reject', reason: `couldn't reach the queue gate (${err.message}) — nothing was saved, try again` }));

          if (validation && !validation.approved && validation.action === 'reject') {
            setActionNotice(`⚠️ Jarvis: ${validation.reason}`);
            setActionLoading(false);
            return;
          }
          if (validation && validation.action === 'merge') {
            setActionNotice(`ℹ️ Jarvis: ${validation.reason}`);
            setActionDone(true);
            setActionLoading(false);
            return;
          }
        }

        endpoint = '/api/tasks/create';
        body = { name: action.name, taskType: action.taskType, dueDate: action.dueDate, priority: action.priority, status: 'Suggested', accountId };

      } else if (action.type === 'create_lead') {
        // ── Jarvis gate: check monitoring status and duplicate leads ──────────
        const leadValidation = await apiFetch('/api/jarvis/validate-write', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create_lead',
            accountId: ctx.accountId || null,
            data: { name: action.name },
          }),
        }).then(r => { if (!r.ok) throw new Error(`validator returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => ({ approved: true }))` — any timeout, 500,
          // or offline browser silently APPROVED the write. An unreachable
          // validator is not an approval. The `r.ok` check matters too: a 401
          // body parses cleanly to {error:'…'}, leaving `approved` undefined,
          // which failed closed only by luck, while a 500 returning HTML threw
          // into the catch and failed open. Both paths are deliberate now.
          .catch(err => ({ approved: false, reason: `couldn't verify this write (${err.message}) — try again` }));

        if (!leadValidation.approved) {
          setActionNotice(`⚠️ Jarvis: ${leadValidation.reason}`);
          setActionLoading(false);
          return;
        }

        endpoint = '/api/leads/create-single';
        body = { name: action.name, accountName: action.accountName, accountId: ctx.accountId || null, signal: action.signal, priority: action.priority, hypothesis: action.hypothesis };

      } else if (action.type === 'create_opportunity') {
        let accountId = ctx.accountId || null;
        if (!accountId && action.accountName) {
          const acctRes = await apiFetch('/api/accounts/list');
          const acctData = await acctRes.json();
          if (acctData.accounts) {
            const nameLower = action.accountName.toLowerCase();
            const match = acctData.accounts.find(a =>
              (a.accountName || '').toLowerCase().includes(nameLower) ||
              nameLower.includes((a.accountName || '').toLowerCase())
            );
            if (match) accountId = match.id;
          }
        }

        // ── Jarvis gate: check account/opportunity status before creating ─────
        if (accountId) {
          const validation = await apiFetch('/api/jarvis/validate-queue-item', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: action.name,
              why: action.hypothesis || 'Opportunity created from Floor session',
              priority: '🟡 Important',
              accountId,
              opportunityId: null, // new opp, no existing opp to check
              source: 'Manual',
            }),
          }).then(r => { if (!r.ok) throw new Error(`gate returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => null)` followed by `if (validation && …)`,
          // so a failed call skipped every check — account-monitoring, dead
          // opportunity, duplicate overlap and the 3-item cap all silently
          // bypassed. Note this endpoint also WRITES the situation_queue row
          // as part of 'validating' (validate-queue-item.js:132-147), so a
          // failure here means the queue item did not land either — the
          // notice has to say so rather than implying only a check was
          // skipped.
          .catch(err => ({ approved: false, action: 'reject', reason: `couldn't reach the queue gate (${err.message}) — nothing was saved, try again` }));

          if (validation && !validation.approved && validation.action === 'reject') {
            setActionNotice(`⚠️ Jarvis: ${validation.reason}`);
            setActionLoading(false);
            return;
          }
          // Note: merge doesn't apply to opportunities — each opp is distinct
        }

        endpoint = '/api/opportunities/create';
        // B92 — the personas emit a CANONICAL stage ("Qualification"); this column
        // holds the PROBABILITY label ("25% - Qualification"), and the handler
        // derives stage_normalized from it. Sending the canonical name straight
        // through stored a value the dropdown cannot render and a null
        // stage_normalized on every Floor-created opportunity.
        body = { accountId, name: action.name, probability: toProbabilityLabel(action.stage), dealSize: action.dealSize, valueHypothesis: action.hypothesis, status: 'Hypothesis' };

      } else if (action.type === 'update_meddic') {
        const oppId = ctx.opportunityId || null;
        if (!oppId) { setActionNotice('No opportunity in scope — open The Floor from an opportunity card first.'); setActionLoading(false); return; }

        // ── Jarvis gate: verify opportunity is alive before writing MEDDIC ────
        const validation = await apiFetch('/api/jarvis/validate-write', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_meddic', opportunityId: oppId,
            data: { meddicMetrics: action.metrics, meddicEconomicBuyer: action.economicBuyer,
                    meddicDecisionCriteria: action.decisionCriteria, meddicDecisionProcess: action.decisionProcess,
                    meddicIdentifiedPain: action.identifiedPain, meddicChampion: action.champion },
          }),
        }).then(r => { if (!r.ok) throw new Error(`validator returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => ({ approved: true }))` — any timeout, 500,
          // or offline browser silently APPROVED the write. An unreachable
          // validator is not an approval. The `r.ok` check matters too: a 401
          // body parses cleanly to {error:'…'}, leaving `approved` undefined,
          // which failed closed only by luck, while a 500 returning HTML threw
          // into the catch and failed open. Both paths are deliberate now.
          .catch(err => ({ approved: false, reason: `couldn't verify this write (${err.message}) — try again` }));

        if (!validation.approved) {
          setActionNotice(`⚠️ Jarvis: ${validation.reason}`);
          setActionLoading(false);
          return;
        }

        endpoint = '/api/opportunities/update';
        body = {
          id:                   oppId,
          meddicMetrics:        action.metrics         || undefined,
          meddicEconomicBuyer:  action.economicBuyer   || undefined,
          meddicDecisionCriteria: action.decisionCriteria || undefined,
          meddicDecisionProcess:  action.decisionProcess  || undefined,
          meddicIdentifiedPain:   action.identifiedPain   || undefined,
          meddicChampion:         action.champion         || undefined,
        };

      } else if (action.type === 'update_business_case') {
        const oppId = ctx.opportunityId || null;
        if (!oppId) { setActionNotice('No opportunity in scope — open The Floor from an opportunity card first.'); setActionLoading(false); return; }

        // ── Jarvis gate: verify opportunity is alive and content is substantive ─
        const validation = await apiFetch('/api/jarvis/validate-write', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_business_case', opportunityId: oppId,
            data: { title: action.title, summary: action.summary, content: action.content },
          }),
        }).then(r => { if (!r.ok) throw new Error(`validator returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => ({ approved: true }))` — any timeout, 500,
          // or offline browser silently APPROVED the write. An unreachable
          // validator is not an approval. The `r.ok` check matters too: a 401
          // body parses cleanly to {error:'…'}, leaving `approved` undefined,
          // which failed closed only by luck, while a 500 returning HTML threw
          // into the catch and failed open. Both paths are deliberate now.
          .catch(err => ({ approved: false, reason: `couldn't verify this write (${err.message}) — try again` }));

        if (!validation.approved) {
          setActionNotice(`⚠️ Jarvis: ${validation.reason}`);
          setActionLoading(false);
          return;
        }

        endpoint = '/api/opportunities/update';
        body = {
          id:   oppId,
          pid:  { businessCase: { title: action.title, summary: action.summary, content: action.content, updatedAt: new Date().toISOString() } },
        };

      } else if (action.type === 'create_derived_signal') {
        // Routes through Jarvis — Jarvis validates source signals and writes
        const res = await apiFetch('/api/jarvis/create-derived-signal', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId:      ctx.accountId || null,
            opportunityId:  ctx.opportunityId || null,
            title:          action.title,
            signalType:     action.signalType,
            confidence:     action.confidence,
            reasoning:      action.reasoning,
            interpretation: action.interpretation,
            sourceSignalIds: action.sourceSignalIds || [],
            generatedBy:    'Victor',
          }),
        });
        const json = await res.json();
        setActionNotice(json.success ? `🧠 Derived signal created — "${(action.title || '').slice(0, 50)}"` : `⚠️ Jarvis: ${json.reason || 'Failed'}`);
        if (json.success) setActionDone(true);
        setActionLoading(false);
        return;

      } else if (action.type === 'convert_lead') {
        const leadIdList = (action.leadIds || '').split(',').map(s => s.trim()).filter(Boolean);

        // ── Jarvis gate: verify lead is convertible ───────────────────────────
        if (leadIdList[0]) {
          const convertValidation = await apiFetch('/api/jarvis/validate-write', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'convert_lead', leadId: leadIdList[0], data: {} }),
          }).then(r => { if (!r.ok) throw new Error(`validator returned ${r.status}`); return r.json(); })
          // B119: was `.catch(() => ({ approved: true }))` — any timeout, 500,
          // or offline browser silently APPROVED the write. An unreachable
          // validator is not an approval. The `r.ok` check matters too: a 401
          // body parses cleanly to {error:'…'}, leaving `approved` undefined,
          // which failed closed only by luck, while a 500 returning HTML threw
          // into the catch and failed open. Both paths are deliberate now.
          .catch(err => ({ approved: false, reason: `couldn't verify this write (${err.message}) — try again` }));

          if (!convertValidation.approved) {
            setActionNotice(`⚠️ Jarvis: ${convertValidation.reason}`);
            setActionLoading(false);
            return;
          }
        }

        endpoint = '/api/leads/convert-to-opportunity';
        body = { leadId: leadIdList[0], leadIds: leadIdList, hypothesis: action.hypothesis, stage: action.stage || 'Prospecting' };

      } else if (action.type === 'table_lead') {
        const leadIdList = (action.leadIds || '').split(',').map(s => s.trim()).filter(Boolean);
        for (const lid of leadIdList) {
          await apiFetch('/api/leads/update/' + lid, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'Tabled', victor_reasoning: action.reason || 'Tabled by Victor' }),
          });
        }
        setActionNotice('✅ Lead tabled — ' + (action.reason || 'Victor recommendation logged'));
        setActionDone(true);
        setActionLoading(false);
        return;
      }

      if (!endpoint) { setActionNotice('⚠️ Unknown action type'); setActionLoading(false); return; }

      // endpoint is always an /api/ path (see the switch above) — apiFetch attaches the JWT.
      const res = await apiFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const json = await res.json();
      const labels = { create_task: 'Task', create_lead: 'Lead', create_opportunity: 'Opportunity' };
      setActionNotice(json.notice || (json.success ? '✅ ' + (labels[action.type] || 'Done') + ' created' : '⚠️ Failed — ' + (json.error || 'try again')));
      if (json.success) setActionDone(true);
    } catch (err) {
      setActionNotice('⚠️ Error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const isSingleClick  = action?.type === 'stage_draft' || action?.type === 'add_calendar';
  const isConfirmClick = action?.type === 'create_task' || action?.type === 'create_lead' || action?.type === 'create_opportunity' || action?.type === 'convert_lead' || action?.type === 'table_lead' || action?.type === 'create_derived_signal';
  const singleLabel    = action?.type === 'stage_draft' ? '🎭 Send to Staging' : '📅 Add to Calendar';
  const confirmLabel   = action?.type === 'create_task' ? '✓ Create Task'
                       : action?.type === 'create_lead' ? '✓ Create Lead'
                       : action?.type === 'convert_lead' ? '⚡ Pursue — Convert Lead'
                       : action?.type === 'table_lead' ? '🗃 Table Lead'
                       : action?.type === 'create_derived_signal' ? '🧠 Create Derived Signal'
                       : '✓ Create Opportunity';

  return (
    <div className={`tf-message ${isUser ? 'tf-message--user' : 'tf-message--ai'}`} data-ai={msg.ai || ''}>
      {!isUser && (
        <div className="tf-ai-header" style={{ color: cfg.color }}>
          <span className="tf-ai-icon" style={{ background: cfg.bg }}>{cfg.icon}</span>
          <span className="tf-ai-name" style={{ color: cfg.color }}>{cfg.name}</span>
          <span className="tf-ai-label">{cfg.label}</span>
          {timeStr && <span className="tf-timestamp">{timeStr}</span>}
        </div>
      )}
      <div className="tf-bubble" style={!isUser ? { borderLeftColor: cfg.color } : {}}>
        {isUser
          ? <span className="tf-user-text">{msg.content}</span>
          : isStreaming
            ? <span className="tf-streaming">{msg.content}<span className="tf-cursor" /></span>
            : <MarkdownContent content={cleanContent || ''} />
        }
        {/* Single-click actions: stage_draft, add_calendar */}
        {isSingleClick && !dismissed && (
          <div className="tf-action-bar">
            {actionNotice
              ? <span className="tf-action-notice">{actionNotice}</span>
              : <>
                  <button className={`tf-action-btn tf-action-btn--primary ${actionDone ? 'tf-action-btn--done' : ''}`} onClick={handleActionClick} disabled={actionLoading || actionDone}>
                    {actionLoading ? '⏳ Working…' : actionDone ? '✅ Done' : singleLabel}
                  </button>
                  <button className="tf-action-btn tf-action-btn--secondary" onClick={() => setDismissed(true)}>Dismiss</button>
                </>
            }
          </div>
        )}
        {/* Confirm/cancel actions: create_task, create_lead, create_opportunity */}
        {isConfirmClick && !dismissed && (
          <div className="tf-action-bar tf-action-bar--confirm">
            {actionNotice
              ? <span className="tf-action-notice">{actionNotice}</span>
              : <>
                  <button className={`tf-action-btn tf-action-btn--confirm ${actionDone ? 'tf-action-btn--done' : ''}`} onClick={handleConfirmAction} disabled={actionLoading || actionDone}>
                    {actionLoading ? '⏳ Creating…' : actionDone ? '✅ Done' : confirmLabel}
                  </button>
                  <button className="tf-action-btn tf-action-btn--secondary" onClick={() => setDismissed(true)}>Cancel</button>
                </>
            }
          </div>
        )}
      </div>
      {isUser && timeStr && (
        <div className="tf-user-time">{timeStr}</div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function TheFloor({ onNavigate, entryContext = {}, returnTo = null }) {
  const { firstName: userName, strategyName } = useProfile();
  // entryContext: { type: 'nav'|'account'|'opportunity'|'contact'|'signal',
  //                accountId, accountName, opportunityId, opportunityName,
  //                contactId, contactName }

  const [thread,         setThread]         = useState([]);
  const [input,          setInput]          = useState('');
  const [loading,        setLoading]        = useState(false);
  const [openingLoading, setOpeningLoading] = useState(false);
  const [activeAI,       setActiveAI]       = useState(null);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [context,        setContext]        = useState(entryContext);
  const [saving,         setSaving]         = useState(false);
  const [learning,       setLearning]       = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [error,          setError]          = useState(null);

  // Pause / Resume / End Session state
  const [showExitModal,  setShowExitModal]  = useState(false);
  const [pendingNav,     setPendingNav]     = useState(null);  // destination to nav to after confirm
  const [resumeBanner,   setResumeBanner]   = useState(null);  // { accountName, pausedAt, thread, context }
  const [endingSummary,  setEndingSummary]  = useState(false); // true while summarize call is in flight

  const threadEndRef   = useRef(null);
  const inputRef       = useRef(null);
  const openingFired   = useRef(false);
  const abortRef       = useRef(null);
  const threadRef      = useRef([]);
  const contextRef     = useRef(entryContext);

  // Keep threadRef in sync for auto-save
  useEffect(() => { threadRef.current = thread; }, [thread]);
  useEffect(() => { contextRef.current = context; }, [context]);

  // ── Phase A.5b: Autosave on unmount ───────────────────────────────────────
  // If the user navigates away from The Floor without clicking End Session
  // or Clear, capture the in-progress conversation. Without this, mid-thread
  // intelligence (Victor's read, MEDDIC updates, contacts mentioned) is lost
  // when the user switches pages.
  //
  // Uses keepalive:true so the request survives page unload. Fire-and-forget;
  // any errors are swallowed because by the time they happen, the user is
  // already on a different page and there's no UI to surface them in.
  useEffect(() => {
    return () => {
      const t = (threadRef.current || []).filter(m => !m.isOpening && m.content);
      if (t.length < 2) return;
      const ctx = contextRef.current || {};
      try {
        apiFetch('/api/warroom/learn', {
          method:    'POST',
          headers:   { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            accountId:     ctx.accountId     || null,
            opportunityId: ctx.opportunityId || null,
            thread:        t,
            context:       ctx,
          }),
        }).catch(() => {});
      } catch {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread, activeAI]);

  // Load context-aware opening message on mount ONLY when coming from a specific context
  // (account page, opportunity card, contact card, signal).
  // Nav opens start blank — Gary initiates the conversation.
  useEffect(() => {
    if (openingFired.current || thread.length > 0) return;
    const hasContext = entryContext && (
      entryContext.accountId ||
      entryContext.opportunityId ||
      entryContext.contactId ||
      entryContext.signal ||
      entryContext.action ||           // Victor queue items from Today
      entryContext.message ||          // direct message context (news panel, signals)
      entryContext.type === 'queue' || // Victor queue items
      entryContext.type === 'alert' || // signal cluster alerts from dashboard
      entryContext.type === 'task'  || // task items from Today
      entryContext.type === 'lead'     // lead evaluation from Leads page
    );
    if (!hasContext) return; // Nav open — stay blank, user starts the conversation
    openingFired.current = true;
    loadOpening();
  }, []); // eslint-disable-line

  // Update context when entryContext changes (e.g. navigating from account)
  useEffect(() => {
    if (entryContext && Object.keys(entryContext).length > 0) {
      setContext(entryContext);
      // If context changes and there's already a thread, don't reload opening
    }
  }, [entryContext?.accountId, entryContext?.opportunityId, entryContext?.contactId]); // eslint-disable-line

  const loadOpening = async () => {
    setOpeningLoading(true);
    try {
      // Direct message context (e.g. from news panel "Ask Victor" / "Draft Outreach")
      // Auto-send the message directly so the AI responds immediately.
      // EXCEPTION: type:'alert' entries skip this even if they have a message —
      // they go through buildAlertOpening for gap assessment + Selma auto-fire.
      if (entryContext.message && entryContext.type !== 'alert') {
        const autoMsg = {
          role:      'user',
          content:   entryContext.message,
          timestamp: new Date().toISOString(),
          id:        'auto-' + Date.now(),
        };
        setThread([autoMsg]);
        setOpeningLoading(false);
        setTimeout(() => sendMessage(entryContext.message), 100);
        return;
      }

      // Queue items: if we have accountId, go through floor/open for a gap-aware
      // Victor opening (gap assessment + Selma tasking). The alert action is
      // passed as alertAction context — Victor uses it as his brief subject.
      // If there's no accountId (generic queue item), fall through to floor/open
      // nav path which gives a standard Victor opening.
      // NOTE: task items always auto-send since they have no account gap to assess.
      if ((entryContext.type === 'queue' || entryContext.type === 'alert') && entryContext.action && !entryContext.accountId) {
        // M4 fix: do NOT setThread([autoMsg]) here — sendMessage adds the user bubble itself.
        // Setting it here caused a duplicate bubble on every Today → Floor transition.
        setOpeningLoading(false);
        setTimeout(() => sendMessage(entryContext.action), 100);
        return;
      }
      // Queue items WITH accountId fall through to floor/open below (gap-aware opening)
      if (entryContext.type === 'task' && entryContext.taskName) {
        // M4 fix: same — sendMessage adds the user bubble, no need to pre-set it.
        setOpeningLoading(false);
        setTimeout(() => sendMessage(`I need to work on this task: ${entryContext.taskName}`), 100);
        return;
      }

      // IMPORTANT: use entryContext (the prop) not context (state copy) here.
      // context state may not yet have type/action/preAnalysis populated at mount time.
      const params = new URLSearchParams();
      if (entryContext.accountId)     params.set('accountId',     entryContext.accountId);
      if (entryContext.opportunityId) params.set('opportunityId', entryContext.opportunityId);
      if (entryContext.contactId)     params.set('contactId',     entryContext.contactId);
      if (entryContext.signal)        params.set('signal',        entryContext.signal);
      params.set('entryContext', entryContext.type || 'nav');
      // Alert/queue entry: pass action, accountName, preAnalysis for gap-aware opening
      if (entryContext.type === 'queue' || entryContext.type === 'alert') {
        if (entryContext.action)         params.set('alertAction',    entryContext.action);
        if (entryContext.accountName)    params.set('accountName',    entryContext.accountName);
        if (entryContext.preAnalysis)    params.set('preAnalysis',    entryContext.preAnalysis.slice(0, 1800));
        if (entryContext.sourceHeadline) params.set('sourceHeadline', entryContext.sourceHeadline.slice(0, 500));
        if (entryContext.sourceUrl)      params.set('sourceUrl',      entryContext.sourceUrl.slice(0, 2000));
      }
      // Lead evaluation entry: pass lead context for Victor's gap assessment
      if (entryContext.type === 'lead') {
        if (entryContext.accountName)    params.set('accountName',    entryContext.accountName);
        if (entryContext.leadIds)        params.set('leadIds',        encodeURIComponent(JSON.stringify(entryContext.leadIds)));
        if (entryContext.leadHypothesis) params.set('leadHypothesis', encodeURIComponent(entryContext.leadHypothesis.slice(0, 800)));
        if (entryContext.leadSignals)    params.set('leadSignals',    encodeURIComponent(JSON.stringify(entryContext.leadSignals)));
        if (entryContext.leadPriority)   params.set('leadPriority',   entryContext.leadPriority);
        // Mark lead as In Review immediately on Floor open
        (entryContext.leadIds || []).forEach(lid => {
          apiFetch('/api/leads/update/' + lid, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'In Review' }),
          }).catch(() => {});
        });
      }

      const res  = await apiFetch('/api/floor/open?' + params.toString());
      const data = await res.json();

      if (data.success && data.message) {
        const openMsg = {
          role:      'assistant',
          ai:        data.ai || 'victor',
          content:   data.message,
          timestamp: new Date().toISOString(),
          id:        'opening-' + Date.now(),
          isOpening: true,
        };
        setThread([openMsg]);
        // Merge any context returned from the opening API
        if (data.context) setContext(prev => ({ ...prev, ...data.context }));

        // For all alert/queue entries with an account, auto-fire Selma immediately.
        // She gets the full alert context — action, source evidence, gaps — so she
        // can start real work without Gary typing anything.
        if ((entryContext.type === 'queue' || entryContext.type === 'alert') && entryContext.accountId) {
          const accountLabel = entryContext.accountName || 'this account';
          const sourceEvidence = data.sourceHeadline
            ? 'The triggering evidence was: "' + data.sourceHeadline + '"' + (data.sourceUrl ? ' — source: ' + data.sourceUrl : '') + '. '
            : (entryContext.sourceHeadline
              ? 'The triggering evidence was: "' + entryContext.sourceHeadline + '"' + (entryContext.sourceUrl ? ' — source: ' + entryContext.sourceUrl : '') + '. '
              : '');
          const gapList = (data.gaps && data.gaps.length > 0)
            ? ' Victor identified these specific gaps: ' + data.gaps.join('; ') + '.'
            : '';
          const selmaMsg = 'Selma — priority alert on ' + accountLabel + '. ' +
            sourceEvidence +
            'I need you to: (1) fetch and summarize the triggering source if a URL is available, ' +
            '(2) identify the key executives at ' + accountLabel + ' relevant to this signal, ' +
            '(3) confirm or update what you know about their technology posture and current initiatives. ' +
            '(4) When you have your findings, hand them directly to ' + strategyName + ' to make the strategic call.' +
            gapList +
            ` Do not ask ${userName || 'the user'} what to research. Start now, deliver your findings, then hand to Victor.`;
          // Add auto-trigger message to thread so done handler preserves it
          // Auto-trigger: send Selma her briefing behind the scenes.
          // role:'system' with isAutoTrigger hides it from Gary's view.
          // sendMessage is called with the content so floor/chat processes it normally,
          // but we don't add it to the visible thread as a user bubble.
          setTimeout(() => { sendMessage(selmaMsg, { hidden: true }); }, 2000);
        }
      }
    } catch (err) {
      console.warn('[TheFloor] opening load failed:', err.message);
    } finally {
      setOpeningLoading(false);
    }
  };

  // Check for paused session on mount
  useEffect(() => {
    apiFetch('/api/floor/resume-session')
      .then(r => r.json())
      .then(data => {
        if (data.found && data.thread?.length >= 2) {
          setResumeBanner({
            accountName: data.context?.accountName || null,
            pausedAt:    data.pausedAt,
            thread:      data.thread,
            context:     data.context || {},
          });
        }
      })
      .catch(() => {}); // Non-blocking
  }, []); // eslint-disable-line

  // Auto-save on unmount (silent fallback — primary path is Pause or End Session)
  useEffect(() => {
    return () => {
      const t = threadRef.current.filter(m => !m.isOpening);
      if (t.length >= 2) {
        apiFetch('/api/victor/save-session', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId:   context.accountId,
            accountName: context.accountName || '',
            messages:    t.map(m => ({ role: m.role, content: m.content || '' })),
            mode:        'floor',
          }),
        }).catch(() => {});
      }
    };
  }, []); // eslint-disable-line

  const sendMessage = useCallback(async (messageOverride, options = {}) => {
    const message = (messageOverride || input).trim();
    if (!message || loading) return;

    setInput('');
    setError(null);
    setLoading(true);
    setActiveAI(null);
    setStreamingMsgId(null);
    setSaved(false);

    // hidden: true means this is a system auto-trigger message — don't add to visible thread
    const isHidden = options.hidden === true;
    const userMsg = { role: 'user', content: message, timestamp: new Date().toISOString(),
                      ...(isHidden ? { isHiddenTrigger: true } : {}) };
    // Use threadRef.current (always fresh) not thread (stale closure).
    // This is critical: when sendMessage is called from a setTimeout (e.g. auto-trigger),
    // the 'thread' closure value is stale. threadRef.current reflects the actual current thread.
    const currentThread = isHidden
      ? [...threadRef.current]          // hidden trigger: don't add user bubble
      : [...threadRef.current, userMsg]; // normal message: show user bubble
    setThread(currentThread);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await apiFetch('/api/floor/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        signal:  controller.signal,
        body: JSON.stringify({
          message,
          thread: threadRef.current.filter(m => !m.isOpening), // Don't send opening msg as history
          context: {
            accountId:       context.accountId,
            accountName:     context.accountName,
            opportunityId:   context.opportunityId,
            opportunityName: context.opportunityName,
            contactId:       context.contactId,
            contactName:     context.contactName,
            entryContext:    context.type || 'nav',
            // Alert provenance — available when Floor opened from a priority alert
            alertAction:     context.action         || entryContext.action         || undefined,
            sourceHeadline:  context.sourceHeadline || entryContext.sourceHeadline || undefined,
            sourceUrl:       context.sourceUrl      || entryContext.sourceUrl      || undefined,
            // Lead evaluation — available when Floor opened from Leads page
            leadIds:         context.leadIds        || entryContext.leadIds        || undefined,
            leadHypothesis:  context.leadHypothesis || entryContext.leadHypothesis || undefined,
          },
        }),
      });

      if (!response.ok) throw new Error(`API error ${response.status}`);

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          let event;
          try { event = JSON.parse(raw); } catch { continue; }

          switch (event.type) {
            case 'thinking':
              setActiveAI(event.ai);
              break;

            case 'context_update':
              // chat.js resolved an account from natural language — anchor this session
              if (event.accountId) {
                setContext(prev => ({
                  ...prev,
                  accountId:   event.accountId,
                  accountName: event.accountName || prev.accountName,
                }));
              }
              break;

            case 'search':
              // Selma is running live searches — show searching indicator in thread
              setThread(prev => {
                const cleaned = prev.filter(m => m.id !== 'selma-searching');
                return [...cleaned, {
                  role: 'system', ai: 'selma',
                  content: event.content || `🔍 Searching: ${(event.targets || []).join(', ')}...`,
                  id: 'selma-searching',
                  timestamp: event.timestamp || new Date().toISOString(),
                  isSearching: true,
                }];
              });
              break;

            case 'message': {
              const msgId = event.timestamp || Date.now().toString();
              setStreamingMsgId(msgId);
              setActiveAI(null);
              setThread(prev => {
                // Remove thinking indicators, searching indicator, and hidden trigger bubbles
                const cleaned = prev.filter(m => !m.thinking && m.id !== 'selma-searching' && !m.isHiddenTrigger);
                return [...cleaned, {
                  role: 'assistant', ai: event.ai,
                  content: event.content, timestamp: event.timestamp, id: msgId,
                }];
              });
              setTimeout(() => setStreamingMsgId(null), 300);
              break;
            }

            case 'handoff':
            case 'system':
              setActiveAI(null);
              setThread(prev => [...prev, {
                role: 'system', ai: 'jarvis', content: event.content,
                timestamp: event.timestamp || new Date().toISOString(),
              }]);
              break;

            case 'done':
              if (event.thread) {
                // Merge server thread with current frontend state.
                // The server thread is authoritative but the frontend may have
                // rendered messages already via 'message' events. We use the
                // server thread as the base but keep any frontend-only messages
                // (like isOpening) that the server doesn't know about.
                setThread(prev => {
                  // Preserve the opening message — server thread doesn't include it
                  const preservedMsgs = prev.filter(m => m.isOpening);
                  // Filter the hidden auto-trigger message from server thread
                  // Server includes the Selma trigger as a user message; Gary must never see it
                  const cleanThread = event.thread.filter(m =>
                    !(m.role === 'user' && typeof m.content === 'string' &&
                      m.content.startsWith('Selma — priority alert on'))
                  );
                  return [...preservedMsgs, ...cleanThread];
                });
              }
              setActiveAI(null);
              setStreamingMsgId(null);
              // Auto-save and learn — fires for all sessions with enough content, with or without accountId
              if (event.thread?.length > 2) {
                setTimeout(() => autoSaveAndLearn(event.thread), 800);
              }
              break;

            case 'error':
              throw new Error(event.message || 'Unknown error');

            default:
              break;
          }
        }
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('[TheFloor]', err.message);
      }
      setActiveAI(null);
      setStreamingMsgId(null);
    } finally {
      setLoading(false);
      setActiveAI(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, thread, loading, context]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoSaveAndLearn = async (threadToSave) => {
    const t = (threadToSave || thread).filter(m => !m.isOpening && m.content);
    if (!t.length) return;
    setSaving(true);
    try {
      // Determine primary AI from thread to route to correct save-session
      const aiCounts = {};
      t.forEach(m => { if (m.ai) aiCounts[m.ai] = (aiCounts[m.ai] || 0) + 1; });
      const primaryAI = Object.entries(aiCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'victor';

      const saveEndpoint = primaryAI === 'selma'  ? '/api/selma/save-session'
                         : primaryAI === 'cyrano' ? '/api/cyrano/save-session'
                         : primaryAI === 'quinn'  ? '/api/quinn/save-session'
                         : '/api/victor/save-session';

      // ── Jarvis session-close runs in parallel with AI-specific save ──────────
      // Jarvis owns the authoritative session record and queue validation.
      // The AI-specific endpoints handle domain work (MEDDIC, research logging).
      // Neither blocks the other — both fire simultaneously.
      const jarvisClose = apiFetch('/api/jarvis/session-close', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread:      t,
          context,
          sessionType: primaryAI === 'selma' ? 'selma' : primaryAI === 'cyrano' ? 'cyrano' : primaryAI === 'quinn' ? 'quinn' : 'floor',
          actionsLog:  [], // future: pass confirmed actions from this session
          proposedQueueItems: [], // Victor's proposed items flow through Jarvis here
        }),
      }).catch(err => console.warn('[TheFloor] jarvis/session-close error:', err.message));

      // saveEndpoint is one of the four /api/*/save-session paths above.
      const aiSave = apiFetch(saveEndpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId:   context.accountId   || null,
          accountName: context.accountName || '',
          thread:      t,
          messages:    t.map(m => ({ role: m.role, content: m.content, ai: m.ai })),
          mode:        'floor',
          context,
        }),
      }).catch(err => console.warn('[TheFloor] ai save-session error:', err.message));

      await Promise.all([jarvisClose, aiSave]);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);

      // Always fire learn — works with or without accountId
      setLearning(true);
      await apiFetch('/api/warroom/learn', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId:    context.accountId    || null,
          opportunityId:context.opportunityId|| null,
          thread:       t.map(m => ({ ...m, ai: m.ai || (m.role === 'assistant' ? primaryAI : undefined) })),
          context,
        }),
      });

      // Fire Selma intel commit — extracts new contacts, core system, tech intel,
      // logs session as activity. Non-blocking: if it fails, session save is unaffected.
      if (context.accountId) {
        apiFetch('/api/selma/commit-intel', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId:    context.accountId,
            accountName:  context.accountName || '',
            sessionThread: t.map(m => ({ role: m.role, ai: m.ai, content: m.content })),
            sessionType:  'floor',
            opportunityId: context.opportunityId || null,
          }),
        }).catch(err => console.warn('[TheFloor] commit-intel error:', err.message));
      }
    } catch (err) {
      console.warn('[TheFloor] save/learn error:', err.message);
    } finally {
      setSaving(false);
      setLearning(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearFloor = () => {
    // Auto-save before clearing
    const t = thread.filter(m => !m.isOpening && m.content);
    if (t.length >= 2) autoSaveAndLearn(t);
    setThread([]);
    setError(null);
    setSaved(false);
    setActiveAI(null);
    openingFired.current = false;
    // Only reload opening if we have account/opportunity/contact context.
    // Nav-level clears stay blank — no unsolicited Victor briefing.
    const hasContext = context.accountId || context.opportunityId || context.contactId || context.signal;
    if (hasContext) setTimeout(loadOpening, 100);
  };

  // ── Pause / End Session / Resume handlers ────────────────────────────────────

  // Called whenever Gary tries to navigate away (Back button or nav item).
  // If there's a live session in progress, intercept and show the exit modal.
  const handleNavigate = (destination) => {
    const liveThread = thread.filter(m => !m.isOpening && m.content);
    if (liveThread.length >= 2) {
      setPendingNav(destination);
      setShowExitModal(true);
    } else {
      // Nothing worth saving — navigate directly
      onNavigate && onNavigate(destination);
    }
  };

  // Pause: write thread + context to Supabase, then navigate away
  const handlePause = async () => {
    const liveThread = thread.filter(m => !m.isOpening && m.content);
    setShowExitModal(false);
    try {
      await apiFetch('/api/floor/pause-session', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread: liveThread, context }),
      });
    } catch (err) {
      console.warn('[TheFloor] pause-session error:', err.message);
    }
    onNavigate && onNavigate(pendingNav);
  };

  // End Session: generate closing summary, append to thread, save, then navigate
  const handleEndSession = async () => {
    const liveThread = thread.filter(m => !m.isOpening && m.content);
    setShowExitModal(false);
    setEndingSummary(true);

    try {
      const resp = await apiFetch('/api/floor/summarize', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread: liveThread, context }),
      });
      const data = await resp.json();

      if (data.success && data.summary) {
        const s = data.summary;
        // Build a readable closing summary message and append to thread
        const parts = [];
        if (s.oneLineSummary) parts.push(s.oneLineSummary);
        if (s.decisions?.length)     parts.push('**Decisions made:**\n' + s.decisions.map(d => '\u2022 ' + d).join('\n'));
        if (s.nextActions?.length)   parts.push('**Next actions:**\n' + s.nextActions.map(a => '\u2022 ' + a).join('\n'));
        if (s.openQuestions?.length) parts.push('**Open questions:**\n' + s.openQuestions.map(q => '\u2022 ' + q).join('\n'));

        const summaryMsg = {
          role:             'assistant',
          ai:               'jarvis',
          content:          parts.join('\n\n'),
          timestamp:        new Date().toISOString(),
          isClosingSummary: true,
        };

        const finalThread = [...liveThread, summaryMsg];
        setThread(prev => [...prev, summaryMsg]);

        // Discard any paused session — this is a deliberate end
        apiFetch('/api/floor/resume-session', { method: 'DELETE' }).catch(() => {});

        // Save with the summary included
        await autoSaveAndLearn(finalThread);
      } else {
        // Summarize failed — save what we have and navigate
        await autoSaveAndLearn(liveThread);
      }
    } catch (err) {
      console.warn('[TheFloor] end-session summarize error:', err.message);
      await autoSaveAndLearn(liveThread);
    } finally {
      setEndingSummary(false);
    }

    onNavigate && onNavigate(pendingNav);
  };

  // Resume: restore thread + context from paused session, clear banner
  const handleResume = () => {
    if (!resumeBanner) return;
    setThread(resumeBanner.thread);
    setContext(resumeBanner.context);
    setResumeBanner(null);
    // Delete the paused session row — it's now live again
    apiFetch('/api/floor/resume-session', { method: 'DELETE' }).catch(() => {});
  };

  // Dismiss resume banner — start fresh, leave paused session in Supabase
  // (Gary can still come back to it from another session open if needed)
  const handleDismissResume = () => {
    setResumeBanner(null);
    // Delete the paused record — Gary consciously chose to start fresh
    apiFetch('/api/floor/resume-session', { method: 'DELETE' }).catch(() => {});
  };

  const isAnyLoading = loading || !!activeAI || openingLoading || endingSummary;


  const activeAICfg = activeAI ? AI_CONFIG[activeAI] : null;

  // Context display label
  const contextLabel = context.opportunityName
    ? `💼 ${context.opportunityName}`
    : context.accountName
      ? `🏢 ${context.accountName}`
      : context.contactName
        ? `👤 ${context.contactName}`
        : null;

  return (
    <div className="tf-container">
      {/* Header */}
      <div className="tf-header">
        <div className="tf-header-left">
          {returnTo && (
            <button
              className="tf-back-btn"
              onClick={() => handleNavigate(returnTo)}
              title={`Back to ${returnTo.charAt(0).toUpperCase() + returnTo.slice(1)}`}
            >
              ← {returnTo.charAt(0).toUpperCase() + returnTo.slice(1)}
            </button>
          )}
          {/* Studio format, as Victor/Cyrano/Selma: a 44px mark, then a stacked
              title + subtitle block. The team pills used to sit here as a third
              item in this row — .tf-brand is flex-row, so name, subtitle and
              five pills all competed for one line. They belong on the right. */}
          <span className="dm-page-mark dm-page-mark--floor">⚡</span>
          <div className="tf-brand">
            <span className="tf-brand-name">The Floor</span>
            <span className="tf-brand-sub">Your AI team, working together in one room</span>
          </div>
        </div>
        <div className="tf-header-right">
          <div className="tf-ai-team-pills">
            {Object.entries(AI_CONFIG).filter(([k]) => k !== 'jarvis').map(([key, cfg]) => (
              <span key={key} className="tf-team-pill" style={{ color: cfg.color, background: cfg.bg }}>
                {cfg.icon} {cfg.name}
              </span>
            ))}
          </div>
          {contextLabel && (
            <span className="tf-context-pill">{contextLabel}</span>
          )}
          {(saving || learning || saved) && (
            <span className={`tf-status-pill ${saving ? 'saving' : learning ? 'learning' : 'done'}`}>
              {saving ? '⏳ Saving...' : learning ? '🧠 Learning...' : '✅ Saved & Learned'}
            </span>
          )}
          {thread.filter(m => !m.isOpening).length > 0 && (
            <>
              <button
                className="tf-end-session-btn"
                onClick={() => { setPendingNav(null); setShowExitModal(true); }}
                disabled={isAnyLoading}
                title="Pause or end this session"
              >
                {endingSummary ? '⏳ Ending...' : '⏹ End Session'}
              </button>
              <button className="tf-clear-btn" onClick={clearFloor} disabled={isAnyLoading}>
                New conversation
              </button>
            </>
          )}
        </div>
      </div>

      {/* Resume Banner — shown when a paused session is waiting */}
      {resumeBanner && (
        <div className="tf-resume-banner">
          <div className="tf-resume-banner-text">
            <span className="tf-resume-icon">⏸</span>
            <span>
              <strong>Paused session{resumeBanner.accountName ? ` — ${resumeBanner.accountName}` : ''}</strong>
              {resumeBanner.pausedAt && (
                <span className="tf-resume-time">
                  {' '}· {new Date(resumeBanner.pausedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </span>
          </div>
          <div className="tf-resume-banner-actions">
            <button className="tf-resume-btn" onClick={handleResume}>Resume</button>
            <button className="tf-resume-discard-btn" onClick={handleDismissResume}>Start fresh</button>
          </div>
        </div>
      )}

      {/* Exit Modal — Pause / End Session / Cancel */}
      {showExitModal && (
        <div className="tf-modal-overlay">
          <div className="tf-modal">
            <div className="tf-modal-header">
              <span className="tf-modal-icon">💬</span>
              <h3>Leave this session?</h3>
            </div>
            <p className="tf-modal-body">
              You have an active conversation in progress. What would you like to do?
            </p>
            <div className="tf-modal-actions">
              <button className="tf-modal-btn tf-modal-btn--pause" onClick={handlePause}>
                <span>⏸</span>
                <span>
                  <strong>Pause</strong>
                  <small>Save and return later to pick up exactly where you left off</small>
                </span>
              </button>
              <button className="tf-modal-btn tf-modal-btn--end" onClick={handleEndSession} disabled={endingSummary}>
                <span>⏹</span>
                <span>
                  <strong>{endingSummary ? 'Generating summary...' : 'End Session'}</strong>
                  <small>Generate a closing summary, save, and close this session</small>
                </span>
              </button>
              <button className="tf-modal-btn tf-modal-btn--cancel" onClick={() => { setShowExitModal(false); setPendingNav(null); }}>
                Cancel — stay on The Floor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thread */}
      <div className="tf-thread">
        {openingLoading && (
          <div className="tf-opening-loading">
            <div className="tf-thinking">
              <span className="tf-thinking-dot" /><span className="tf-thinking-dot" /><span className="tf-thinking-dot" />
            </div>
          </div>
        )}

        {thread.map((msg, i) => (
          <MessageBubble
            key={msg.id || msg.timestamp || i}
            msg={msg}
            isStreaming={streamingMsgId === (msg.id || msg.timestamp)}
            context={context}
          />
        ))}

        {/* Active AI thinking indicator */}
        {activeAICfg && (
          <div className="tf-message tf-message--ai" data-ai={activeAI}>
            <div className="tf-ai-header" style={{ color: activeAICfg.color }}>
              <span className="tf-ai-icon" style={{ background: activeAICfg.bg }}>{activeAICfg.icon}</span>
              <span className="tf-ai-name" style={{ color: activeAICfg.color }}>{activeAICfg.name}</span>
              <span className="tf-ai-label">{activeAICfg.label}</span>
            </div>
            <div className="tf-thinking">
              <span className="tf-thinking-dot" /><span className="tf-thinking-dot" /><span className="tf-thinking-dot" />
            </div>
          </div>
        )}

        {error && (
          <div className="tf-error">⚠️ {error} <button onClick={() => setError(null)}>Dismiss</button></div>
        )}
        <div ref={threadEndRef} />
      </div>

      {/* Input */}
      <div className="tf-input-area">
        <div className="tf-input-row">
          <textarea
            ref={inputRef}
            className="tf-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Talk to your team — Victor, Selma, and Cyrano are listening..."
            rows={2}
            disabled={isAnyLoading}
          />
          <button
            className="tf-send-btn"
            onClick={() => sendMessage()}
            disabled={isAnyLoading || !input.trim()}
          >
            {isAnyLoading ? '⏳' : '→'}
          </button>
        </div>
        <div className="tf-input-hint">
          Address <strong>Victor</strong>, <strong>Selma</strong>, or <strong>Cyrano</strong> directly — or just say what you need
        </div>
      </div>
    </div>
  );
}

export default TheFloor;
